import cn from 'classnames'
import { HTMLProps } from 'react'

import lock from '/images/illustrations/lock@2x.webp?url'
import lock2x from '/images/illustrations/lock@2x.webp?url'
import { HomepageAboutDiv } from '~/components/pages/homepage/About'

interface HomepagePrivacyMethodsProps extends HTMLProps<HTMLDivElement> {}

export function HomepagePrivacyMethods ({ className }: HomepagePrivacyMethodsProps)
{
    const items = [{
        title: 'RingCT',
        text: 'Hides the transaction amount using ring signatures.',
    }, {
        title: 'Stealth Addresses',
        text: 'Generates unique addresses for each recipient to unlink wallets.',
    }, {
        title: 'Zero-Knowledge Proofs (zk-SNARKs)',
        text: 'Proves a transaction is valid without revealing details.',
    }, {
        title: 'CoinJoin',
        text: 'Mixes multiple transactions to break traceability.',
    }, {
        title: 'Dandelion++',
        text: 'Hides the origin of a transaction on the network level.',
    }, {
        title: 'MimbleWimble',
        text: 'Compresses the blockchain with no addresses or amounts.',
    }]

    return (
        <section id={'privacy-methods'} className={cn('flex flex-col gap-4', 'xl:grid grid-cols-[1fr_2fr]', className)}>
            <HomepageAboutDiv
                className={cn(
                    'flex flex-row gap-5 justify-between items-start',
                    'px-4 py-6',
                    'md:px-10 md:py-4 md:gap-9',
                    'border border-accent/10',
                    'xl:flex-col',
                )}
            >
                <h2 className={cn('leading-[103%] uppercase whitespace-nowrap')}>
                    <span aria-hidden={true}>[</span>Privacy Methods<span aria-hidden={true}>]</span>
                </h2>

                <img
                    src={lock}
                    srcSet={`${lock2x} 2x`}
                    alt={`ASCII art of physical lock`}
                    width={175}
                    height={175}
                    loading={'lazy'}
                    fetchPriority={'low'}
                    decoding={'async'}
                    className={cn('shrink-1 order-1 w-full h-auto max-w-44', 'lg:max-w-none md:order-2 lg:w-44')}
                />
            </HomepageAboutDiv>

            <HomepageAboutDiv className={cn('md:px-10 md:py-4', 'md:border md:border-accent/10')}>
                <h3
                    className={cn('text-start leading-[103%] text-balance font-medium uppercase')}
                >Main privacy methods:</h3>

                <ul
                    className={cn(
                        'mt-4',
                        'flex flex-col gap-2 justify-stretch  flex-wrap',
                        'md:mt-8',
                        'md:flex-row md:justify-start md:items-stretch md:gap-6',
                        '*:basis-0 *:grow-1 *:shrink-1 md:*:min-w-68',
                        'xl:*:min-w-57',
                    )}
                >
                    {items.map((item, index) => (
                        <HomepagePrivacyMethodListItem
                            key={index}
                            index={index + 1} {...item}
                            className={cn('xl:h-50')}
                        />
                    ))}
                </ul>
            </HomepageAboutDiv>
        </section>
    )
}

interface HomepagePrivacyMethodListItemProps extends HTMLProps<HTMLLIElement>
{
    index: number
    title: string
    text: string
}

function HomepagePrivacyMethodListItem ({
    index,
    title,
    text,
    className,
}: HomepagePrivacyMethodListItemProps)
{
    return (
        <li
            className={cn(
                'flex flex-col justify-between items-stretch gap-5',
                'px-4 py-6 rounded-lg',
                'md:gap-4',
                'md:px-5 md:py-6',
                'border border-accent/10',
                'md:border-dashed md:border-accent/40',
                className,
            )}
        >
            <div aria-hidden={true} className={cn('hidden order-3', 'md:block')}></div>
            {/*<p className={cn('hidden order-3 font-medium text-accent/80', 'md:block')}>*/} {/*    <span aria-hidden={true}>[</span>{index}<span aria-hidden={true}>]</span>*/} {/*</p>*/}
            <h3 className={cn('order-1 font-medium uppercase text-accent/80 leading-[103%] text-balance')}>{title}</h3>
            <p
                className={cn('order-2 font-light text-accent/80 tracking-tight leading-[normal] text-balance')}
            >{text}</p>
        </li>
    )
}